// src/api/healthcare/ClinikoFormImportIssueApi.ts
/**
 * @file The Cliniko form-import issue ledger API (S11, ClickUp 14ygtkj65bx)
 * @module api/clinikoFormImportIssueApi
 *
 * ═══ ⚠️ `VITE_HEALTHCARE_SERVER` ALREADY ENDS IN `/api` ══════════════════════════════════════
 * Every ConfigMap sets it to `https://healthcare.<host>/api`, so URLs are built as
 * `${API_BASE_URL}/practitioner/cliniko/...` and never `${API_BASE_URL}/api/practitioner/...`.
 * Adding the prefix would 404 in every environment — and identically in dev, so nothing local
 * would catch it.
 *
 * ═══ PAGING IS SERVER-SIDE AND NOT OPTIONAL ══════════════════════════════════════════════════
 * A clinic's first full import can open thousands of rows. The server caps `per_page`, so there
 * is deliberately no "fetch them all" call here to reach for.
 */

import { ENV } from "../../../config/runtimeEnv";

import {
	ClinikoFormImportIssueListResponse,
	ClinikoFormImportIssueResolveResponse,
	ClinikoFormImportIssueStatus,
	ClinikoFormImportIssueType,
} from "../../../types/healthcare/ClinikoFormImportIssueTypes";
import { http } from "../../../lib/http/client";

const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

/** This repo has NO axios interceptor, so every module attaches the bearer by hand. */

export interface ClinikoFormImportIssueQuery {
	/** Omit for the server's default, which is `unresolved`. */
	status?: ClinikoFormImportIssueStatus;
	issue_type?: ClinikoFormImportIssueType;
	cliniko_template_id?: number;
	page?: number;
	per_page?: number;
}

/** One page of the ledger, newest sighting first. */
export const listFormImportIssues = async (
	query: ClinikoFormImportIssueQuery = {}
): Promise<ClinikoFormImportIssueListResponse> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/cliniko/form-import-issues`, {
		params: query,
	});
	return response.data;
};

/**
 * Mark one issue handled.
 *
 * ⚠️ **Idempotent — a second call is a double-click, not an error.** It answers 200 with
 * `already_resolved: true` and the ORIGINAL resolver, so a repeat must never be surfaced as a
 * failure.
 */
export const resolveFormImportIssue = async (issueId: number): Promise<ClinikoFormImportIssueResolveResponse> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/cliniko/form-import-issues/${issueId}/resolve`,
		{}
	);
	return response.data;
};
